import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  FormLabel,
  Stack,
} from "@mui/joy";
import { ApiCompetition, ApiCompetitionDetail } from "griptonite-api-client";
import { HTTPError } from "griptonite-api-client/client";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { FormProvider, useController, useForm, useFormContext, useWatch } from "react-hook-form";
import { usePrevious } from "react-use";

import { Route } from "@/core/types";
import { useCompetitionRoutesQuery, useUpdateRouteMutation } from "@/hooks/routes";
import { transformValidationErrorToErrorsArray } from "@/utils/handleFormError";

import { useCompetitionContext } from "../CompetitionOverview/CompetitionProvider";
import CheckboxSelectInput from "../forms/CheckboxSelect";
import FormGroup from "../forms/FormGroup";
import { NumberSelect, NumberSelectInput } from "../forms/NumberSelectInput";

type FormValues = {
  categoryUuids: string[];
  routeGroupMapping: Record<string, { number: number | null; name: string | null }>;
  number: number | null;
};

const NumberSelector = ({
  route,
  competition,
}: {
  route: Route;
  competition: ApiCompetition & Partial<ApiCompetitionDetail>;
}) => {
  const { data: routes } = useCompetitionRoutesQuery(competition.uuid, competition.venues[0].uuid);
  const selectedCategoryUuids = useWatch({ name: "categoryUuids" });
  const currentNumber = route.circuitNumber;
  const takenNumbers = useMemo(() => {
    const routeGroups = competition.routeGroups.filter((g) =>
      g.categories.some((uuid) => selectedCategoryUuids.includes(uuid)),
    );
    const matchingRoutes = routes?.filter((route) =>
      route.competitionRouteGroups.some((group) =>
        routeGroups.some((rg) => rg.uuid === group.uuid),
      ),
    );
    if (!matchingRoutes) {
      return [];
    }
    return [
      ...new Set(
        matchingRoutes
          .map((route) => route.circuitNumber)
          .filter((x) => x !== undefined && x !== null && x !== currentNumber),
      ),
    ] as number[];
  }, [routes, selectedCategoryUuids, competition.routeGroups, currentNumber]);

  return <NumberSelectInput label="Number" name="number" takenNumbers={takenNumbers} />;
};

export default function RouteCategoryForm({
  route,
  competition,
}: {
  route?: Route;
  competition?: ApiCompetition & Partial<ApiCompetitionDetail>;
}) {
  // This is the global selected category uuid
  const { selectedCategoryUuid: competitionSelectedCategoryUuid, setCategoryEditorDirty } =
    useCompetitionContext();

  const initialCategoryUuids = useMemo(() => {
    if (!route) {
      return undefined;
    }
    return [...new Set(route.competitionRouteGroups.flatMap((x) => x.categories))];
  }, [route]);

  const methods = useForm({
    defaultValues: {
      categoryUuids: initialCategoryUuids || [],
      routeGroupMapping: route?.displayRouteGroupMapping || {},
      number: route?.circuitNumber ?? null,
    },
  });

  useLayoutEffect(() => {
    setCategoryEditorDirty(methods.formState.isDirty);
    if (methods.formState.isDirty) {
      const onBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = "";
      };
      addEventListener("beforeunload", onBeforeUnload);
      return () => {
        removeEventListener("beforeunload", onBeforeUnload);
        setCategoryEditorDirty(false);
      };
    }
  }, [methods.formState.isDirty, setCategoryEditorDirty]);

  const categoryOptions = useMemo(() => {
    return competition?.stages[0].categories.map((category) => ({
      name: category.name,
      value: category.uuid,
    }));
  }, [competition]);

  const selectedCategoryUuids: string[] | undefined = methods.watch("categoryUuids");
  const filteredCategoryOptions = categoryOptions?.filter((x) =>
    selectedCategoryUuids?.includes(x.value),
  );

  const prevCompetitionSelectedCategoryUuid = usePrevious(competitionSelectedCategoryUuid);

  useEffect(() => {
    methods.reset({
      categoryUuids: initialCategoryUuids,
      routeGroupMapping: route?.displayRouteGroupMapping || {},
      number: route?.circuitNumber ?? null,
    });
  }, [methods, initialCategoryUuids, route]);

  const [selectedCategoryUuid, setSelectedCategoryUuid] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!selectedCategoryUuid && selectedCategoryUuids?.length) {
      const newUuid = competitionSelectedCategoryUuid || selectedCategoryUuids[0];
      setSelectedCategoryUuid(newUuid);
    }
    if (selectedCategoryUuid && !selectedCategoryUuids?.includes(selectedCategoryUuid)) {
      setSelectedCategoryUuid(selectedCategoryUuids?.[0]);
    }
    if (
      competitionSelectedCategoryUuid &&
      competitionSelectedCategoryUuid !== prevCompetitionSelectedCategoryUuid &&
      selectedCategoryUuids?.includes(competitionSelectedCategoryUuid)
    ) {
      setSelectedCategoryUuid(competitionSelectedCategoryUuid);
    }
  }, [
    selectedCategoryUuid,
    competitionSelectedCategoryUuid,
    prevCompetitionSelectedCategoryUuid,
    selectedCategoryUuids,
  ]);

  const mutation = useUpdateRouteMutation(route?.uuid);

  if (!route || !competition || !categoryOptions || !filteredCategoryOptions) {
    return <CircularProgress />;
  }

  const onSubmit = async (formValues: FormValues) => {
    try {
      await mutation.mutateAsync({
        ...(competition.settings?.hasDynamicRouteNumbering
          ? {
              displayRouteGroupMapping: formValues.routeGroupMapping,
            }
          : {
              circuitNumber: formValues.number,
            }),
        competitionRouteGroups: competition.routeGroups
          .filter((g) => g.categories.some((uuid) => formValues.categoryUuids.includes(uuid)))
          .map((g) => g.uuid),
      });
    } catch (error) {
      if (error instanceof HTTPError && error.response.status === 400 && error.jsonData) {
        const errors = transformValidationErrorToErrorsArray(error);
        errors.forEach((error) => {
          if (error.path === "circuitNumber") {
            error.path = "number";
          } else if (error.path === "displayRouteGroupMapping") {
            error.path = "routeGroupMapping";
          }
          // oxlint-disable-next-line typescript/no-explicit-any
          methods.setError(error.path as any, {
            type: error.code,
            message: error.message,
          });
        });
      }
    }
  };

  return (
    <FormProvider {...methods}>
      <Stack
        component="form"
        autoComplete="off"
        sx={{
          paddingX: 1.5,
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
        }}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        {competition.autoRouteGroupMode !== "SINGLE" ? (
          <Box sx={{ marginBottom: 1.5, marginTop: 1 }}>
            <CheckboxSelectInput
              options={categoryOptions}
              name="categoryUuids"
              label="Which categories will try this route?"
            />
          </Box>
        ) : null}
        <Box sx={{ flex: 1 }}>
          <FormGroup>
            {competition.settings?.hasDynamicRouteNumbering ? (
              <>
                <FormLabel>Select the number for each category:</FormLabel>
                <AccordionGroup>
                  {selectedCategoryUuids.map((uuid) => {
                    const category = competition.stages[0].categories.find((x) => x.uuid === uuid);
                    if (!category) {
                      return null;
                    }
                    return (
                      <Accordion
                        key={uuid}
                        title={category?.name}
                        expanded={selectedCategoryUuid === uuid}
                        onChange={() => {
                          setSelectedCategoryUuid(uuid);
                        }}
                        sx={{
                          marginX: -1.5,
                          ".MuiAccordionSummary-root": {
                            paddingX: 1.5,
                            backgroundColor: "transparent",
                          },
                          ".MuiAccordionDetails-root": {
                            paddingX: 1.5,
                            fontSize: 16,
                          },
                          "&:last-child": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <AccordionSummary>{category.name}</AccordionSummary>
                        <AccordionDetails>
                          {selectedCategoryUuid === uuid ? (
                            <DynamicNumberSelector
                              category={uuid}
                              route={route}
                              competition={competition}
                            />
                          ) : null}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </AccordionGroup>
                {/* <PillsSelect
                  value={selectedCategoryUuid || null}
                  onChange={(v) => {
                    if (v) {
                      setSelectedCategoryUuid(v);
                    }
                  }}
                  options={filteredCategoryOptions}
                />
                <Box sx={{ marginTop: 1, marginBottom: 1 }}>
                  {selectedCategoryUuid ? (
                    <DynamicNumberSelector
                      category={selectedCategoryUuid}
                      route={route}
                      competition={competition}
                    />
                  ) : null}
                </Box> */}
              </>
            ) : (
              <NumberSelector competition={competition} route={route} />
            )}
          </FormGroup>
        </Box>
        {methods.formState.isDirty && (
          <Button type="submit" loading={methods.formState.isSubmitting} loadingPosition="start">
            Save
          </Button>
        )}
      </Stack>
    </FormProvider>
  );
}

const DynamicNumberSelector = ({
  category,
  route,
  competition,
}: {
  category: string;
  route: Route;
  competition: ApiCompetition;
}) => {
  const { data: routes } = useCompetitionRoutesQuery(competition.uuid, competition.venues[0].uuid);
  const routeGroup = competition.routeGroups.find((x) =>
    x.categories.includes(category) ? x : null,
  );

  const { control } = useFormContext();
  const { field } = useController({
    name: "routeGroupMapping",
    control,
  });

  const value = routeGroup && field.value?.[routeGroup?.uuid]?.number;
  const currentNumber = routeGroup && route.displayRouteGroupMapping[routeGroup.uuid]?.number;

  const routeGroupRoutes = useMemo(
    () => routes?.filter((x) => x.competitionRouteGroups.some((y) => y.uuid === routeGroup?.uuid)),
    [routes, routeGroup],
  );

  const takenNumbers = useMemo(() => {
    if (!routeGroupRoutes || !routeGroup) {
      return [];
    }
    return routeGroupRoutes
      .map((route) => route.displayRouteGroupMapping[routeGroup.uuid]?.number)
      .filter((x) => x !== undefined && x !== currentNumber) as number[];
  }, [routeGroupRoutes, routeGroup, currentNumber]);

  return (
    <NumberSelect
      takenNumbers={takenNumbers}
      initialCount={Math.max(16, (routeGroupRoutes?.length || 0) + 1)}
      value={value}
      onChange={(value) =>
        routeGroup &&
        field.onChange({
          ...field.value,
          [routeGroup.uuid]: {
            ...field.value?.[routeGroup.uuid],
            number: value,
            name: `${routeGroup.name} ${value}`,
          },
        })
      }
    />
  );
};
